import { env } from "@/config/env";
import { CognitoError } from "@/types/auth";

/**
 * Talks to Cognito directly over HTTPS (docs/SPEC.md section 2.4): no hosted UI, this
 * app has its own sign-in page. `X-Amz-Target` selects the operation; the body is
 * plain JSON (`application/x-amz-json-1.1`).
 */

/** Without an answer by then, sign-in reports a network error instead of waiting forever. */
const COGNITO_TIMEOUT_MS = 15_000;

const ENDPOINT = () => `https://cognito-idp.${env.cognitoRegion}.amazonaws.com/`;

async function call<T>(action: string, body: Record<string, unknown>): Promise<T> {
  let response: Response;
  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), COGNITO_TIMEOUT_MS);
  try {
    response = await fetch(ENDPOINT(), {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": `AWSCognitoIdentityProviderService.${action}`,
      },
      body: JSON.stringify(body),
      signal: timeout.signal,
    });
  } catch {
    throw new CognitoError("NETWORK_ERROR", "The network request failed.");
  } finally {
    clearTimeout(timer);
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code = (data.__type ?? "UnknownError").split("#").pop() ?? "UnknownError";
    throw new CognitoError(code, data.message ?? code);
  }
  return data as T;
}

interface AuthResultWire {
  AuthenticationResult?: {
    IdToken: string;
    AccessToken: string;
    RefreshToken?: string;
    ExpiresIn: number;
  };
  ChallengeName?: "NEW_PASSWORD_REQUIRED";
  Session?: string;
  ChallengeParameters?: Record<string, string>;
}

export interface AuthOutcome {
  tokens?: { idToken: string; accessToken: string; refreshToken: string; expiresAt: number };
  challenge?: { name: "NEW_PASSWORD_REQUIRED"; session: string; username: string };
}

function fromWire(wire: AuthResultWire, fallbackRefreshToken?: string): AuthOutcome {
  if (wire.ChallengeName === "NEW_PASSWORD_REQUIRED") {
    return { challenge: { name: "NEW_PASSWORD_REQUIRED", session: wire.Session ?? "", username: "" } };
  }
  const result = wire.AuthenticationResult;
  if (!result) throw new CognitoError("UNKNOWN_RESPONSE", "Cognito returned no tokens and no challenge.");
  return {
    tokens: {
      idToken: result.IdToken,
      accessToken: result.AccessToken,
      refreshToken: result.RefreshToken ?? fallbackRefreshToken ?? "",
      expiresAt: Date.now() + result.ExpiresIn * 1000,
    },
  };
}

export async function initiateAuth(username: string, password: string): Promise<AuthOutcome> {
  const wire = await call<AuthResultWire>("InitiateAuth", {
    AuthFlow: "USER_PASSWORD_AUTH",
    ClientId: env.cognitoClientId,
    AuthParameters: { USERNAME: username, PASSWORD: password },
  });
  const outcome = fromWire(wire);
  if (outcome.challenge) outcome.challenge.username = username;
  return outcome;
}

export async function respondToNewPasswordChallenge(
  username: string,
  newPassword: string,
  session: string,
): Promise<AuthOutcome> {
  const wire = await call<AuthResultWire>("RespondToAuthChallenge", {
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    ClientId: env.cognitoClientId,
    ChallengeResponses: { USERNAME: username, NEW_PASSWORD: newPassword },
    Session: session,
  });
  return fromWire(wire);
}

export async function refreshTokens(refreshToken: string): Promise<AuthOutcome> {
  const wire = await call<AuthResultWire>("InitiateAuth", {
    AuthFlow: "REFRESH_TOKEN_AUTH",
    ClientId: env.cognitoClientId,
    AuthParameters: { REFRESH_TOKEN: refreshToken },
  });
  return fromWire(wire, refreshToken);
}

export async function updateUserAttributes(
  accessToken: string,
  attributes: Record<string, string>,
): Promise<void> {
  await call("UpdateUserAttributes", {
    AccessToken: accessToken,
    UserAttributes: Object.entries(attributes).map(([Name, Value]) => ({ Name, Value })),
  });
}

export async function getUserAttributeVerificationCode(accessToken: string, attributeName: string): Promise<void> {
  await call("GetUserAttributeVerificationCode", { AccessToken: accessToken, AttributeName: attributeName });
}

export async function verifyUserAttribute(
  accessToken: string,
  attributeName: string,
  code: string,
): Promise<void> {
  await call("VerifyUserAttribute", { AccessToken: accessToken, AttributeName: attributeName, Code: code });
}

export async function globalSignOut(accessToken: string): Promise<void> {
  await call("GlobalSignOut", { AccessToken: accessToken });
}
